import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { XMarkIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { salesApiService } from '../../api/salesApi';
import MultiSelectDropdown from '../../components/ui/MultiSelectDropdown';
import ResponsiveModal from '../ui/ResponsiveModal';

const emptyFormState = () => ({
  hotel_id: '',
  type_id: '',
  date_type_id: '',
  start_date: '',
  finish_date: '',
  market_id: '',
  operator_id: '',
  roomtype_ids: [],
  male_only_id: '2',
  agency_ids: [],
  notes: ''
});

const normalizeId = (value) =>
  value !== undefined && value !== null && value !== '' ? String(value) : '';

/** SalesMarket listesinde adı ALL olan kayıt (büyük/küçük harf duyarsız). */
const findAllMarket = (markets = []) =>
  markets.find((m) => String(m.market || '').trim().toUpperCase() === 'ALL');

/** Dropdown: ALL en üstte, sonra diğerleri alfabetik. */
const sortMarketsAllFirst = (markets = []) =>
  [...markets].sort((a, b) => {
    const aAll = String(a.market || '').trim().toUpperCase() === 'ALL' ? 0 : 1;
    const bAll = String(b.market || '').trim().toUpperCase() === 'ALL' ? 0 : 1;
    if (aAll !== bAll) return aAll - bAll;
    return String(a.market || '').localeCompare(String(b.market || ''), 'tr', { sensitivity: 'base' });
  });

/** Çoklu acente: "tümü" seçimi (API’de ayrı kayıt yok; kayıtta agency null). */
const ALL_AGENCIES_SELECT_ID = 0;

const buildAgencyOptionsWithAll = (agencies = []) => {
  const rest = [...agencies].sort((a, b) =>
    String(a.agency_name || '').localeCompare(String(b.agency_name || ''), 'tr', { sensitivity: 'base' })
  );
  return [{ id: ALL_AGENCIES_SELECT_ID, agency_name: 'All' }, ...rest];
};

/**
 * Map API / list row to modal form state. Resolves FK ids; falls back to name fields when needed.
 */
const buildFormDataFromEntry = (entry, { dateTypesData = [], agenciesData = [], roomTypesData = [] } = {}) => {
  const rawDateType =
    entry.date_type && typeof entry.date_type === 'object' && entry.date_type.id != null
      ? entry.date_type.id
      : entry.date_type;
  let date_type_id = normalizeId(rawDateType ?? entry.date_type_id);
  if (!date_type_id && entry.date_type_name) {
    const name = String(entry.date_type_name).trim();
    const dt = dateTypesData.find(
      (d) =>
        String(d.date_type || '').trim() === name ||
        String(d.date_type || '').toLowerCase() === name.toLowerCase()
    );
    if (dt) date_type_id = String(dt.id);
  }

  const rawOpenStop =
    entry.openstoptype && typeof entry.openstoptype === 'object' && entry.openstoptype.id != null
      ? entry.openstoptype.id
      : entry.openstoptype;
  const type_id = normalizeId(rawOpenStop ?? entry.openstoptype_id);

  const rawHotel =
    entry.hotel && typeof entry.hotel === 'object' && entry.hotel.id != null ? entry.hotel.id : entry.hotel;
  const hotel_id = normalizeId(rawHotel ?? entry.hotel_id);

  const rawMarket =
    entry.market && typeof entry.market === 'object' && entry.market.id != null ? entry.market.id : entry.market;
  const market_id = normalizeId(rawMarket ?? entry.market_id);

  let roomtype_ids = [];
  const rawRoomtype =
    entry.roomtype && typeof entry.roomtype === 'object' && entry.roomtype.id != null
      ? entry.roomtype.id
      : entry.roomtype;
  if (rawRoomtype != null && rawRoomtype !== '') {
    roomtype_ids = [String(rawRoomtype)];
  } else if (entry.roomtype_id != null && entry.roomtype_id !== '') {
    roomtype_ids = [String(entry.roomtype_id)];
  } else if (entry.roomtype_name && roomTypesData.length) {
    const name = String(entry.roomtype_name).trim();
    const rt = roomTypesData.find(
      (r) =>
        String(r.roomtype_name || '').trim().toLowerCase() === name.toLowerCase()
    );
    if (rt) roomtype_ids = [String(rt.id)];
  }

  const rawMaleOnly =
    entry.maleonly && typeof entry.maleonly === 'object' && entry.maleonly.id != null
      ? entry.maleonly.id
      : entry.maleonly;
  let male_only_id = normalizeId(rawMaleOnly ?? entry.male_only_id ?? entry.is_maleonly);

  let agency_ids = [];
  if (entry.agency != null && entry.agency !== '') {
    agency_ids = [String(entry.agency)];
  } else if (entry.agency_name) {
    const name = String(entry.agency_name).trim();
    const nameLower = name.toLowerCase();
    if (nameLower === 'all' || nameLower === 'tümü' || nameLower === 'hepsi') {
      agency_ids = [String(ALL_AGENCIES_SELECT_ID)];
    } else {
      const ag = agenciesData.find(
        (a) => String(a.agency_name || '').trim().toLowerCase() === nameLower
      );
      if (ag) agency_ids = [String(ag.id)];
    }
  } else {
    agency_ids = [String(ALL_AGENCIES_SELECT_ID)];
  }

  return {
    hotel_id,
    type_id,
    date_type_id,
    start_date: entry.start_date || '',
    finish_date: entry.finish_date || '',
    market_id,
    operator_id: normalizeId(
      entry.operator && typeof entry.operator === 'object' && entry.operator.id != null
        ? entry.operator.id
        : entry.operator ?? entry.operator_id
    ),
    roomtype_ids,
    male_only_id: male_only_id || '2',
    agency_ids,
    notes: entry.note ?? entry.notes ?? ''
  };
};

const StopSaleModal = ({ isOpen, onClose, onSave, initialData }) => {
  const [formData, setFormData] = useState({
    hotel_id: '',
    type_id: '',
    date_type_id: '',
    start_date: '',
    finish_date: '',
    market_id: '',
    operator_id: '',
    roomtype_ids: [], // Çoklu room type seçimi
    male_only_id: '2', // Varsayılan olarak 2 ID'sini seç (genellikle Normal)
    agency_ids: [], // Çoklu acente seçimi
    notes: ''
  });

  const [referenceData, setReferenceData] = useState({
    hotels: [],
    openStopTypes: [],
    dateTypes: [],
    markets: [],
    operators: [],
    roomTypes: [],
    maleOnly: [],
    agencies: [] // Yeni acente verisi
  });

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal açıkken scroll kilitle
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Modal açıldığında referans verileri yükle; düzenlemede varsayılanları kaydın üzerine yazma
  useEffect(() => {
    if (isOpen) {
      loadReferenceData(initialData);
    }
  }, [isOpen, initialData]);

  const loadReferenceData = async (editEntry) => {
    try {
      setLoading(true);

      // API çağrılarını tek tek test et
      let hotelsRes, openStopTypesRes, dateTypesRes, marketsRes, operatorsRes, roomTypesRes, maleOnlyRes, agenciesRes;

      try {
        hotelsRes = await salesApiService.getHotels();
      } catch (err) {
        // Silently handle error
        throw new Error(`Hotels API failed: ${err.message}`);
      }

      try {
        openStopTypesRes = await salesApiService.getOpenStopTypes();
      } catch (err) {
        // Silently handle error
        throw new Error(`OpenStopTypes API failed: ${err.message}`);
      }

      try {
        dateTypesRes = await salesApiService.getDateTypes();
      } catch (err) {
        // Silently handle error
        throw new Error(`DateTypes API failed: ${err.message}`);
      }

      try {
        marketsRes = await salesApiService.getMarkets();
      } catch (err) {
        // Silently handle error
        throw new Error(`Markets API failed: ${err.message}`);
      }

      try {
        operatorsRes = await salesApiService.getOperators();
      } catch (err) {
        // Silently handle error
        throw new Error(`Operators API failed: ${err.message}`);
      }

      try {
        roomTypesRes = await salesApiService.getRoomTypes();
      } catch (err) {
        // Silently handle error
        throw new Error(`RoomTypes API failed: ${err.message}`);
      }

      try {
        maleOnlyRes = await salesApiService.getMaleOnly();
      } catch (err) {
        // Silently handle error
        throw new Error(`MaleOnly API failed: ${err.message}`);
      }

      try {
        agenciesRes = await salesApiService.getAgencies();
      } catch (err) {
        // Silently handle error
        throw new Error(`Agencies API failed: ${err.message}`);
      }

      // Her response için array kontrolü yap
      const hotelsData = Array.isArray(hotelsRes) ? hotelsRes : (hotelsRes.data || []);
      const openStopTypesData = Array.isArray(openStopTypesRes) ? openStopTypesRes : (openStopTypesRes.data || []);
      const dateTypesData = Array.isArray(dateTypesRes) ? dateTypesRes : (dateTypesRes.data || []);
      const marketsData = Array.isArray(marketsRes) ? marketsRes : (marketsRes.data || []);
      const operatorsData = Array.isArray(operatorsRes) ? operatorsRes : (operatorsRes.data || []);
      const roomTypesData = Array.isArray(roomTypesRes) ? roomTypesRes : (roomTypesRes.data || []);
      const maleOnlyData = Array.isArray(maleOnlyRes) ? maleOnlyRes : (maleOnlyRes.data || []);
      const agenciesData = Array.isArray(agenciesRes) ? agenciesRes : (agenciesRes.data || []);

      setReferenceData({
        hotels: hotelsData,
        openStopTypes: openStopTypesData,
        dateTypes: dateTypesData,
        markets: marketsData,
        operators: operatorsData,
        roomTypes: roomTypesData,
        maleOnly: maleOnlyData,
        agencies: agenciesData
      });




      let defaultValues = {};
      if (!editEntry) {
        defaultValues = {};

        if (hotelsData.length > 0) {
          defaultValues.hotel_id = hotelsData[0].id.toString();
        }

        if (marketsData.length > 0) {
          const allMarket = findAllMarket(marketsData);
          defaultValues.market_id = String((allMarket ?? marketsData[0]).id);
        }

        if (roomTypesData.length > 0 && defaultValues.hotel_id) {
          const hotelRoomTypes = roomTypesData.filter(
            (rt) => rt.hotel_id && rt.hotel_id.toString() === defaultValues.hotel_id
          );

          if (hotelRoomTypes.length > 0) {
            defaultValues.roomtype_ids = [hotelRoomTypes[0].id.toString()];
          } else {
            const allOption = roomTypesData.find(
              (rt) =>
                rt.roomtype_name &&
                (rt.roomtype_name.toLowerCase() === 'all' ||
                  rt.roomtype_name.toLowerCase() === 'tümü' ||
                  rt.roomtype_name.toLowerCase() === 'hepsi')
            );

            if (allOption) {
              defaultValues.roomtype_ids = [allOption.id.toString()];
            } else if (roomTypesData.length > 0) {
              defaultValues.roomtype_ids = [roomTypesData[0].id.toString()];
            }
          }
        }

        const stopSalesOption = openStopTypesData.find(
          (item) =>
            item.type &&
            (item.type.toLowerCase().includes('stop sales') ||
              item.type.toLowerCase().includes('stop sale') ||
              item.type.toLowerCase().includes('stopsales') ||
              item.type.toLowerCase().includes('stopsale'))
        );

        if (stopSalesOption) {
          defaultValues.type_id = stopSalesOption.id.toString();
        }

        const stayOption = dateTypesData.find(
          (item) =>
            item.date_type &&
            (item.date_type.toLowerCase().includes('stay') ||
              item.date_type.toLowerCase().includes('konaklama'))
        );

        if (stayOption) {
          defaultValues.date_type_id = stayOption.id.toString();
        }

        if (maleOnlyData.length > 0) {
          const normalOption = maleOnlyData.find(
            (item) => item.is_maleonly && item.is_maleonly.toLowerCase().includes('normal')
          );

          const nonMaleOption = maleOnlyData.find(
            (item) => item.is_maleonly && !item.is_maleonly.toLowerCase().includes('male')
          );

          const selectedOption = normalOption || nonMaleOption || maleOnlyData[1] || maleOnlyData[0];

          if (selectedOption) {
            defaultValues.male_only_id = selectedOption.id.toString();
          }
        }

        defaultValues.agency_ids = [String(ALL_AGENCIES_SELECT_ID)];
      }

      if (editEntry) {
        setFormData(
          buildFormDataFromEntry(editEntry, {
            dateTypesData,
            agenciesData,
            roomTypesData
          })
        );
      } else {
        setFormData({
          ...emptyFormState(),
          ...defaultValues
        });
      }
    } catch (err) {
      // Silently handle error

      // Kullanıcıya daha açıklayıcı hata mesajı göster
      alert(`Veri yüklenirken hata oluştu: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Eski handler'lar artık gerekli değil - MultiSelectDropdown kendi onChange'ini kullanıyor

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };

      if (name === 'hotel_id') {
        const hotelRoomTypes = referenceData.roomTypes.filter(
          (rt) => rt.hotel_id && rt.hotel_id.toString() === value
        );

        if (hotelRoomTypes.length > 0) {
          newData.roomtype_ids = [hotelRoomTypes[0].id.toString()];
        } else {
          const allOption = referenceData.roomTypes.find(
            (rt) =>
              rt.roomtype_name &&
              (rt.roomtype_name.toLowerCase() === 'all' ||
                rt.roomtype_name.toLowerCase() === 'tümü' ||
                rt.roomtype_name.toLowerCase() === 'hepsi')
          );

          if (allOption) {
            newData.roomtype_ids = [allOption.id.toString()];
          } else {
            newData.roomtype_ids = [];
          }
        }
      }

      return newData;
    });
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);

      // Küçük bir gecikme ekle - form state'inin güncellenmesi için
      await new Promise(resolve => setTimeout(resolve, 100));

      // Form validasyonu - çoklu seçim için güncellendi
      const requiredFields = {
        hotel_id: 'Hotel',
        roomtype_ids: 'Room Type', // roomtype_id yerine roomtype_ids
        market_id: 'Market',
        date_type_id: 'Date Type',
        type_id: 'Type',
        male_only_id: 'Male Only',
        start_date: 'Start Date',
        finish_date: 'Finish Date'
      };

      const missingFields = [];
      Object.entries(requiredFields).forEach(([field, label]) => {
        const value = formData[field];

        // Çoklu seçim alanları için özel kontrol
        if (field === 'roomtype_ids' || field === 'agency_ids') {
          if (!value || value.length === 0) {
            missingFields.push(label);
          }
        } else if (field === 'male_only_id' || field === 'market_id') {
          // Otomatik seçilen alanlar için özel kontrol
          if (!value || value === '' || value === '0') {
            missingFields.push(label);
          }
        } else {
          if (!value || value === '' || value === '0' || value === 0) {
            missingFields.push(label);
          }
        }
      });

      if (missingFields.length > 0) {
        alert(`Lütfen şu alanları doldurun: ${missingFields.join(', ')}`);
        return;
      }

      // Tarih validasyonu
      if (formData.start_date && formData.finish_date) {
        if (new Date(formData.start_date) > new Date(formData.finish_date)) {
          alert('Başlangıç tarihi bitiş tarihinden sonra olamaz!');
          return;
        }
      }

      // Edit modu için tek kayıt güncelleme
      if (initialData) {
        const updateData = {
          id: initialData.id, // Update için ID
          hotel: formData.hotel_id,
          roomtype: formData.roomtype_ids[0], // Edit modunda tek seçim varsayıyoruz
          market: formData.market_id,
          date_type: formData.date_type_id,
          openstoptype: formData.type_id,
          maleonly: formData.male_only_id,
          operator: formData.operator_id || null,
          agency:
            formData.agency_ids?.some((id) => String(id) === String(ALL_AGENCIES_SELECT_ID))
              ? null
              : formData.agency_ids[0] || null,
          start_date: formData.start_date,
          finish_date: formData.finish_date,
          note: formData.notes
        };
        await onSave(updateData);
        onClose();
        return;
      }

      // Çoklu acente ve room type için çoklu kayıt oluştur

      const includesAllAgencies = formData.agency_ids?.some(
        (id) => String(id) === String(ALL_AGENCIES_SELECT_ID)
      );
      const selectedAgencies = includesAllAgencies
        ? [{ agency_name: null, id: null }]
        : formData.agency_ids
            ?.map((agencyId) => referenceData.agencies.find((a) => a.id.toString() === agencyId))
            .filter(Boolean) || [];

      const selectedRoomTypes = formData.roomtype_ids?.map(roomTypeId =>
        referenceData.roomTypes.find(rt => rt.id.toString() === roomTypeId)
      ).filter(Boolean) || [];


      // Eğer hiçbir şey seçilmemişse varsayılan kayıt oluştur
      if (selectedAgencies.length === 0 && selectedRoomTypes.length === 0) {
        selectedAgencies.push({ agency_name: null });
        selectedRoomTypes.push({ id: null, roomtype_name: null });
      } else if (selectedAgencies.length === 0) {
        selectedAgencies.push({ agency_name: null });
      } else if (selectedRoomTypes.length === 0) {
        selectedRoomTypes.push({ id: null, roomtype_name: null });
      }

      // Her acente ve room type kombinasyonu için kayıt oluştur
      const saveDataArray = [];
      selectedAgencies.forEach(agency => {
        selectedRoomTypes.forEach(roomType => {
          saveDataArray.push({
            hotel: formData.hotel_id,
            roomtype: roomType.id,
            market: formData.market_id,
            date_type: formData.date_type_id,
            openstoptype: formData.type_id,
            maleonly: formData.male_only_id,
            operator: formData.operator_id || null,
            agency_name: agency.agency_name,
            agency: agency.id != null ? agency.id : null,
            start_date: formData.start_date,
            finish_date: formData.finish_date,
            note: formData.notes,
            is_sent: false
          });
        });
      });

      // Çoklu kayıt: tek istekte toplu kayıt (tek özet mail); tek satır: eski tek create
      if (saveDataArray.length > 1) {
        await onSave(saveDataArray);
      } else {
        await onSave(saveDataArray[0]);
      }
      onClose();
    } catch (err) {
      // Silently handle error
      alert(`Kayıt sırasında hata oluştu: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Tüm lookup/select ve çoklu seçim tetikleyicileri Date Type ile aynı görünüm */
  const lookupFieldSurfaceClass =
    'w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
  const lookupFieldClass = `${lookupFieldSurfaceClass} h-9 min-h-[36px]`;
  const notesFieldClass = `${lookupFieldSurfaceClass} resize-none min-h-[4.5rem]`;
  const labelClass = 'text-xs font-medium text-gray-700 dark:text-gray-300';
  const lookupMultiTriggerClass = `${lookupFieldClass} flex items-center justify-between gap-2`;

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      overlayClassName="z-[9999]"
      className="!max-w-5xl w-[min(100%,calc(100vw-2rem))]"
    >
      <div className="flex flex-col max-h-[90vh] w-full bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white pr-2">
            {initialData ? 'Stop Sale Düzenle' : 'New Open & Stop Sale'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto overflow-x-visible flex-1 px-4 sm:px-5 py-3 min-h-0 [scrollbar-gutter:stable]">
          {loading && (
            <div className="text-center py-6 text-sm text-gray-600 dark:text-gray-400">Veriler yükleniyor...</div>
          )}

          {!loading && (
            <form
              id="stop-sale-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSave();
              }}
              className="space-y-4"
            >
              {/* Hotel */}
              <div className="space-y-1">
                <Label htmlFor="hotel_id" className={labelClass}>
                  Hotel
                </Label>
                <select
                  id="hotel_id"
                  name="hotel_id"
                  value={formData.hotel_id}
                  onChange={handleInputChange}
                  className={lookupFieldClass}
                >
                  <option value="">Select Hotel</option>
                  {referenceData.hotels.map((hotel) => (
                    <option key={hotel.id} value={hotel.id}>
                      {hotel.hotel_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type and Date Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="type_id" className={labelClass}>
                    Type
                  </Label>
                  <select
                    id="type_id"
                    name="type_id"
                    value={formData.type_id}
                    onChange={handleInputChange}
                    className={lookupFieldClass}
                  >
                    {referenceData.openStopTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="date_type_id" className={labelClass}>
                    Date Type
                  </Label>
                  <select
                    id="date_type_id"
                    name="date_type_id"
                    value={formData.date_type_id}
                    onChange={handleInputChange}
                    className={lookupFieldClass}
                  >
                    {referenceData.dateTypes.map((dateType) => (
                      <option key={dateType.id} value={dateType.id}>
                        {dateType.date_type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Dates */}
              <div className="space-y-1">
                <Label className={labelClass}>
                  Dates
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="start_date" className="text-[10px] text-gray-600 dark:text-gray-400">
                      Start Date
                    </Label>
                    <div className="relative">
                      <Input
                        id="start_date"
                        name="start_date"
                        type="date"
                        value={formData.start_date}
                        onChange={handleInputChange}
                        className={`!h-9 !min-h-0 ${lookupFieldClass} pr-9`}
                      />
                      <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="finish_date" className="text-[10px] text-gray-600 dark:text-gray-400">
                      Finish Date
                    </Label>
                    <div className="relative">
                      <Input
                        id="finish_date"
                        name="finish_date"
                        type="date"
                        value={formData.finish_date}
                        onChange={handleInputChange}
                        className={`!h-9 !min-h-0 ${lookupFieldClass} pr-9`}
                      />
                      <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Market & Male Only */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="min-w-0">
                  <MultiSelectDropdown
                    selectionMode="single"
                    label="Market"
                    labelClassName={labelClass}
                    triggerClassName={lookupMultiTriggerClass}
                    options={sortMarketsAllFirst(referenceData.markets)}
                    selectedValues={(() => {
                      if (formData.market_id === '' || formData.market_id == null) return [];
                      const n = parseInt(formData.market_id, 10);
                      return Number.isFinite(n) ? [n] : [];
                    })()}
                    onChange={(values) => {
                      const id = values[0];
                      setFormData((prev) => ({
                        ...prev,
                        market_id: id != null && id !== '' ? String(id) : '',
                      }));
                    }}
                    placeholder="Market seçiniz"
                    displayKey="market"
                    valueKey="id"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="male_only_id" className={labelClass}>
                    Male Only
                  </Label>
                  <select
                    id="male_only_id"
                    name="male_only_id"
                    value={formData.male_only_id}
                    onChange={handleInputChange}
                    className={lookupFieldClass}
                  >
                    {referenceData.maleOnly.map((male) => (
                      <option key={male.id} value={male.id}>
                        {male.is_maleonly}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="hidden">
                  <Label htmlFor="operator_id" className={labelClass}>
                    Operator
                  </Label>
                  <select
                    id="operator_id"
                    name="operator_id"
                    value={formData.operator_id}
                    onChange={handleInputChange}
                    className={lookupFieldClass}
                  >
                    <option value="">Select Operator</option>
                    {referenceData.operators.map((operator) => (
                      <option key={operator.id} value={operator.id}>
                        {operator.operator}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Room Type & Acente — yan yana, native select ile aynı davranış (alt kapat butonu yok) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="min-w-0">
                  <MultiSelectDropdown
                    label="Room Type"
                    labelClassName={labelClass}
                    triggerClassName={lookupMultiTriggerClass}
                    options={referenceData.roomTypes.filter(
                      (roomType) =>
                        !formData.hotel_id ||
                        Number(roomType.hotel_id ?? roomType.hotel) === parseInt(formData.hotel_id, 10)
                    )}
                    selectedValues={formData.roomtype_ids?.map((id) => parseInt(id, 10)) || []}
                    onChange={(values) =>
                      setFormData((prev) => ({ ...prev, roomtype_ids: values.map((v) => v.toString()) }))
                    }
                    placeholder="Room Type Seçiniz"
                    displayKey="roomtype_name"
                    valueKey="id"
                  />
                </div>
                <div className="min-w-0">
                  <MultiSelectDropdown
                    label="Acente"
                    labelClassName={labelClass}
                    triggerClassName={lookupMultiTriggerClass}
                    options={buildAgencyOptionsWithAll(referenceData.agencies)}
                    selectedValues={formData.agency_ids?.map((id) => parseInt(id, 10)) || []}
                    onChange={(values) => {
                      const nums = values.map((v) => Number(v));
                      const next = nums.includes(ALL_AGENCIES_SELECT_ID) ? [ALL_AGENCIES_SELECT_ID] : nums;
                      setFormData((prev) => ({ ...prev, agency_ids: next.map(String) }));
                    }}
                    placeholder="Acente Seçiniz"
                    displayKey="agency_name"
                    valueKey="id"
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-1">
                <Label htmlFor="notes" className={labelClass}>
                  Notes
                </Label>
                <textarea
                  id="notes"
                  name="notes"
                  rows={3}
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Notlarınızı buraya yazın..."
                  className={notesFieldClass}
                />
              </div>
            </form>
          )}
        </div>

        <div className="shrink-0 px-4 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="px-4 py-2 text-sm h-9">
            İptal
          </Button>
          <Button
            type="submit"
            form="stop-sale-form"
            disabled={isSubmitting || loading}
            className="px-4 py-2 h-9 text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default StopSaleModal;